/**
 * PostHog — Analytics initialization and event tracking for FairPath.
 *
 * Tracks key funnel events: property search -> dashboard view -> wizard
 * progression -> evidence selection -> checkout -> payment -> download.
 *
 * The API key is a publishable key (safe in frontend code).
 *
 * CONSENT-GATED:
 * - The module may be imported before consent, but initPostHog is called only
 *   after an explicit Accept choice or when a prior Accept choice is present.
 * - Declining leaves PostHog, autocapture, and session recording disabled.
 *
 * LAZY SDK:
 * - This module is the only importer of `posthog-js`, and it imports the SDK
 *   dynamically. The `posthog` bundle chunk is therefore fetched when
 *   initPostHog() decides to initialize (after consent), never on page load.
 * - Everything else reads the SDK through postHogIfLoaded(), which returns the
 *   instance only once it is initialized and null otherwise. Callers must
 *   no-op or queue on null; they must never import `posthog-js` themselves,
 *   because a static import anywhere on the eager path drags the chunk back
 *   into the entry bundle before consent.
 * - Work that components and auth hand us before the SDK is ready — super
 *   properties, the signed-in identity, the persistence upgrade — is held
 *   here and applied once the SDK initializes and capture is allowed, before
 *   the queued analytics events are flushed. Retention follows the same rule
 *   as the event queue in analytics.ts: nothing is held without accepted
 *   consent, and a consent denial discards what is held.
 */

import type { PostHog } from "posthog-js";
import {
  getAnalyticsAudience,
  getAnalyticsConsentRevision,
  hasAcceptedAnalyticsConsent,
  subscribeAnalyticsPermission,
} from "./analyticsAudience";
import { captureAttribution, getAttribution } from "./attribution";
import { preparePostHogPersistence, sanitizePostHogCapture, sanitizePostHogFlagProperties } from "./posthogPrivacy";
import { isSafeBrowserTelemetryIdentity, sanitizeTelemetryValue } from "./telemetryPrivacy";
import {
  isPrivateTelemetrySession,
  observeTelemetrySessionPrivacy,
  onPrivateTelemetrySession,
} from "./telemetrySessionPrivacy";
import {
  getBrowserAnalyticsEnvironmentContext,
  isBrowserAnalyticsCaptureAllowed,
  isBrowserAnalyticsEnvironmentAllowed,
} from "./analyticsContext";

/** The SDK instance once its chunk has been fetched (loaded, not yet necessarily initialized). */
let sdk: PostHog | null = null;
let sdkPromise: Promise<PostHog> | null = null;
/** A chunk fetch started by initPostHog is still in flight. */
let loadPending = false;
/** Whether posthog.init has already been called (guard against double-init). */
let initialized = false;
let loaded = false;
let permissionObserved = false;
let transportGuardInstalled = false;
let transportGeneration = 0;
const requestGenerations = new WeakMap<object, string>();
const captureGenerations = new WeakMap<object, string>();
const currentTransportGeneration = () => `${transportGeneration}:${getAnalyticsConsentRevision()}`;
const readyCallbacks: Array<() => void> = [];

type IdentifyProperties = {
  email?: string;
  created_at?: string;
  has_paid?: boolean;
};
/** Identity payload with the email already hashed; never holds a raw email. */
type PreparedIdentity = {
  userId: string;
  properties: { created_at?: string; has_paid?: boolean; email?: string };
};

/** Deferred SDK work handed to us before the SDK was ready. Latest wins. */
let pendingIdentity: PreparedIdentity | null = null;
let pendingSuperProperties: Record<string, unknown> | null = null;
let persistenceUpgradeRequested = false;
let observedConsentRevision = 0;

const DIRECT_MAIL_CAMPAIGN_ID = "wave1-2026-nophoto-a2";

/**
 * Fetch the SDK chunk and resolve its singleton instance. The result is not
 * initialized; only initPostHog() may call `init`, because that is where the
 * consent, environment, and audience guards live. Only initialization paths
 * (and tests that need the raw instance) should call this. Everyone else
 * uses postHogIfLoaded().
 */
export function getPostHog(): Promise<PostHog> {
  if (!sdkPromise) {
    sdkPromise = import("posthog-js")
      .then((module) => {
        sdk = module.default;
        return module.default;
      })
      .catch((error: unknown) => {
        // Let a transient chunk failure retry on the next initialization attempt.
        sdkPromise = null;
        throw error;
      });
  }
  return sdkPromise;
}

/**
 * Synchronous handle to the SDK, available only once it has been fetched AND
 * initialized. Returns null before consent, before the chunk arrives, and in
 * every environment where initPostHog declined to initialize.
 */
export function postHogIfLoaded(): PostHog | null {
  return sdk?.__loaded ? sdk : null;
}

function getDirectMailCodeFromPath(): string | null {
  const match = window.location.pathname.match(/^\/(?:a|check)\/([^/?#]+)/);
  return match?.[1]?.toUpperCase() ?? null;
}

function registerDirectMailAttribution(posthog: PostHog): boolean {
  const pathCode = getDirectMailCodeFromPath();

  let storedCode: string | null = null;
  let storedCampaignId: string | null = null;
  let storedChannel: string | null = null;
  let storedCountyId: string | null = null;

  try {
    storedCode = sessionStorage.getItem("fp_mail_code") || localStorage.getItem("fp_mail_code");
    storedCampaignId =
      sessionStorage.getItem("fp_mail_campaign_id") ||
      localStorage.getItem("fp_mail_campaign_id");
    storedChannel =
      sessionStorage.getItem("fp_acquisition_channel") ||
      localStorage.getItem("fp_acquisition_channel");
    storedCountyId =
      sessionStorage.getItem("fp_county_id") ||
      localStorage.getItem("fp_county_id");
  } catch {
    // Storage can be unavailable in private/browser-restricted contexts.
  }

  const mailCode = pathCode || storedCode;
  if (!mailCode) return false;

  const mailCampaignId = storedCampaignId || DIRECT_MAIL_CAMPAIGN_ID;
  const acquisitionChannel = storedChannel || "direct_mail";

  try {
    sessionStorage.setItem("fp_mail_code", mailCode);
    sessionStorage.setItem("fp_mail_campaign_id", mailCampaignId);
    sessionStorage.setItem("fp_acquisition_channel", acquisitionChannel);
    localStorage.setItem("fp_mail_code", mailCode);
    localStorage.setItem("fp_mail_campaign_id", mailCampaignId);
    localStorage.setItem("fp_acquisition_channel", acquisitionChannel);
  } catch {
    // Non-critical; PostHog registration below still covers this session.
  }

  posthog.register({
    acquisition_channel: acquisitionChannel,
    mail_campaign_id: mailCampaignId,
    campaign_id: mailCampaignId,
    county_id: storedCountyId,
    utm_source: "dm",
  });

  return Boolean(pathCode);
}

/** An excluded or unresolved session never sends; the analytics queue drops on it too. */
function isAudienceTerminal(): boolean {
  const audience = getAnalyticsAudience();
  return audience === "excluded_internal_or_qa" || audience === "unresolved";
}

/**
 * Consent, environment, and audience are checked before retention, exactly
 * like the analytics event queue. A pending audience is the one state worth
 * waiting through, because auth resolves it moments later.
 */
function mayRetainDeferredWork(): boolean {
  return hasAcceptedAnalyticsConsent() && isBrowserAnalyticsEnvironmentAllowed() && !isAudienceTerminal();
}

function discardDeferredSdkWork(): void {
  pendingIdentity = null;
  pendingSuperProperties = null;
  persistenceUpgradeRequested = false;
}

/**
 * Apply what components and auth handed us while the SDK was not ready. Runs
 * before the ready event so queued analytics events carry the super
 * properties and identity they would have carried with a synchronous SDK.
 */
function applyDeferredSdkWork(posthog: PostHog): void {
  if (pendingSuperProperties) {
    const properties = pendingSuperProperties;
    pendingSuperProperties = null;
    posthog.register(properties);
  }
  if (pendingIdentity) {
    const identity = pendingIdentity;
    pendingIdentity = null;
    applyIdentity(posthog, identity);
  }
  if (persistenceUpgradeRequested) {
    persistenceUpgradeRequested = false;
    upgradePostHogPersistence();
  }
}

function flushReadyCallbacks(posthog: PostHog) {
  if (!isBrowserAnalyticsCaptureAllowed()) return;
  loaded = true;
  applyDeferredSdkWork(posthog);
  try {
    (window as typeof window & { __FAIRPATH_POSTHOG_READY__?: boolean }).__FAIRPATH_POSTHOG_READY__ = true;
    window.dispatchEvent(new Event("fairpath:posthog-ready"));
  } catch {
    // Analytics readiness notifications must never affect app behavior.
  }
  while (readyCallbacks.length > 0) {
    const cb = readyCallbacks.shift();
    try {
      cb?.();
    } catch {
      // Analytics callbacks must never affect application behavior.
    }
  }
}

export function onPostHogReady(callback: () => void) {
  if (isBrowserAnalyticsCaptureAllowed() && (loaded || sdk?.__loaded)) {
    callback();
    return;
  }
  readyCallbacks.push(callback);
}

function syncAnalyticsPermission(): void {
  // A denial (in this document or observed from another tab) or an excluded /
  // unresolved audience discards deferred work the same way analytics.ts
  // discards its queued events.
  const consentRevision = getAnalyticsConsentRevision();
  if (consentRevision !== observedConsentRevision || !hasAcceptedAnalyticsConsent() || isAudienceTerminal()) {
    observedConsentRevision = consentRevision;
    discardDeferredSdkWork();
  }
  // Before init (or while the SDK chunk is still in flight) there is nothing to
  // reconfigure; initPostHog re-evaluates permission when it actually runs.
  if (!initialized || !sdk) { initPostHog(); return; }
  const posthog = sdk;
  const allowed = isBrowserAnalyticsCaptureAllowed();
  const privateSession = isPrivateTelemetrySession();
  posthog.set_config({
    autocapture: allowed && !privateSession,
    capture_pageview: allowed,
    capture_pageleave: allowed,
    disable_session_recording: !allowed || privateSession,
    advanced_disable_flags: !allowed,
    disable_external_dependency_loading: !allowed,
  });
  if (!allowed) {
    transportGeneration += 1;
    loaded = false;
    (window as typeof window & { __FAIRPATH_POSTHOG_READY__?: boolean }).__FAIRPATH_POSTHOG_READY__ = false;
    posthog.stopSessionRecording();
    // Distributed SDK queue names are mangled. Invalidate capture/body objects
    // instead of touching those queues or removing their connectivity listeners.
    posthog.__request_queue = [];
  } else if (posthog.__loaded) {
    flushReadyCallbacks(posthog);
  }
}

function observeAnalyticsPermission(): void {
  if (permissionObserved) return;
  permissionObserved = true;
  observedConsentRevision = getAnalyticsConsentRevision();
  subscribeAnalyticsPermission(syncAnalyticsPermission);
}

function installAnalyticsTransportGuard(posthog: PostHog): void {
  if (transportGuardInstalled) return;
  transportGuardInstalled = true;
  // Installed SDK 1.392.0 batches and retries invoke this typed method at send
  // time, including unload. before_send alone runs too early to stop old batches.
  const sendRequest = posthog._send_request.bind(posthog);
  posthog._send_request = (options) => {
    // Distributed SDK retries preserve the body object while copying options.
    // Keep the generation outside serialized options/events and SDK persistence.
    const body = options.data;
    const currentGeneration = currentTransportGeneration();
    const generation = body && typeof body === "object"
      ? requestGenerations.get(body) ?? currentGeneration : currentGeneration;
    if (body && typeof body === "object") requestGenerations.set(body, generation);
    if (!isBrowserAnalyticsCaptureAllowed() ||
        generation !== currentGeneration) {
      // Settle a denied flags request without a retry or a stuck in-flight latch.
      options.callback?.({ statusCode: 200, text: "{}", json: {} });
      return;
    }
    // The distributed SDK preserves CaptureResult references in batch arrays.
    // Remove only stale captures so fresh eligible events in a mixed batch survive.
    const currentCapture = (capture: object) => {
      const capturedAt = captureGenerations.get(capture);
      return capturedAt === undefined || capturedAt === currentGeneration;
    };
    const currentData = Array.isArray(body) ? body.filter(currentCapture) : body;
    if ((Array.isArray(currentData) && currentData.length === 0) ||
        (body && !Array.isArray(body) && !currentCapture(body))) {
      options.callback?.({ statusCode: 200, text: "{}", json: {} });
      return;
    }
    sendRequest({ ...options, data: currentData, callback: options.callback ? (response) => {
      options.callback?.(isBrowserAnalyticsCaptureAllowed() && generation === currentTransportGeneration()
        ? response : { statusCode: 200, text: "{}", json: {} });
    } : undefined });
  };
}

function readCookieConsent(): string | null {
  try {
    return localStorage.getItem("fairpath_cookie_consent");
  } catch {
    return null;
  }
}

export function initPostHog() {
  // Register before returning for pending auth so consented normal sessions can
  // initialize after eligibility resolves. This never changes cookie consent.
  observeAnalyticsPermission();
  if (initialized || loadPending) return;

  // Keep localhost, preview, and unexpected-host activity out of the
  // customer analytics project entirely. Their fail-closed classification is
  // still serialized to server requests for operational telemetry.
  if (!isBrowserAnalyticsCaptureAllowed()) return;

  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  if (!apiKey) return;

  // initPostHog is consent-gated by the caller. Keep the storage check as a
  // fail-closed defense against accidental initialization elsewhere.
  const consent = readCookieConsent();

  if (consent !== "accepted") return;

  if (!sdk) {
    // Every guard above passed, so this is the first moment the SDK chunk is
    // worth fetching. Consent or permission can change while it is in flight,
    // so re-run the guards from the top once it arrives instead of trusting
    // the answer computed before the fetch.
    loadPending = true;
    void getPostHog().then(
      () => { loadPending = false; initPostHog(); },
      () => { loadPending = false; },
    );
    return;
  }

  startPostHog(sdk, apiKey);
}

function startPostHog(posthog: PostHog, apiKey: string): void {
  initialized = true;
  installAnalyticsTransportGuard(posthog);
  observeTelemetrySessionPrivacy();
  const privateSession = isPrivateTelemetrySession();
  const persistencePrepared = preparePostHogPersistence(apiKey);
  // capture()/people.set() update flag properties before before_send runs.
  // Guard the public SDK setter so fresh person properties share the boundary.
  const setFlagProperties = posthog.setPersonPropertiesForFlags.bind(posthog);
  posthog.setPersonPropertiesForFlags = (properties, reloadFlags) => {
    if (!isBrowserAnalyticsCaptureAllowed()) return;
    setFlagProperties(sanitizePostHogFlagProperties(properties), reloadFlags);
  };
  const suppressPrivatePostHog = () => {
    if (!posthog.__loaded || !isPrivateTelemetrySession()) return;
    posthog.set_config({ autocapture: false, disable_session_recording: true });
    posthog.stopSessionRecording();
  };
  onPrivateTelemetrySession(suppressPrivatePostHog);

  const isDirectMailEntry = /^\/(?:a|check)\/[^/]+/.test(window.location.pathname);

  posthog.init(apiKey, {
    api_host: "https://us.i.posthog.com",
    // For legacy property-code routes, register coarse mail attribution before
    // the first pageview so `/a/:code` is not reported as homepage traffic.
    capture_pageview: !isDirectMailEntry,
    capture_pageleave: true,
    autocapture: !privateSession,
    persistence: persistencePrepared ? "localStorage" : "memory",
    // Defense in depth for SDK-generated pageviews/autocapture. The initial
    // URL is already stripped before SDK initialization, and this hook also
    // redacts credentials that might appear in nested generated properties.
    before_send: (capture) => {
      const safe = sanitizePostHogCapture(capture);
      if (safe) captureGenerations.set(safe, currentTransportGeneration());
      return safe;
    },
    // Private sessions remain suppressed regardless of remote project settings.
    disable_session_recording: privateSession,
    session_recording: {
      // Defense in depth: property identity is also present in text and map/image attributes.
      blockSelector: "[data-private-property-check]",
      maskAllInputs: true,            // PII safety — masks all form inputs
      maskTextSelector: "[data-mask]", // selective masking for sensitive elements
    },
    loaded: () => {
      if (!isBrowserAnalyticsCaptureAllowed()) { syncAnalyticsPermission(); return; }
      // A private navigation can be latched while the SDK is still loading.
      // Recheck here so that transition cannot miss replay/autocapture shutdown.
      suppressPrivatePostHog();
      // Flags requests do not pass through before_send. Seed/sanitize their
      // initial context before SDK capture and remote configuration begins.
      posthog.register({
        $initial_person_info: sanitizeTelemetryValue(
          posthog.get_property("$initial_person_info") ?? {
            r: document.referrer || "$direct",
            u: window.location.href,
          },
        ),
      });
      // Register the fail-closed environment classification for subsequent
      // events. before_send applies the same context to the initial pageview.
      posthog.register(getBrowserAnalyticsEnvironmentContext());

      // SOC-09: Capture UTM + referrer attribution as super properties and
      // person properties after the SDK is actually ready.
      captureAttribution(posthog);

      const registeredDirectMailEntry = registerDirectMailAttribution(posthog);

      if (registeredDirectMailEntry) {
        posthog.capture("$pageview");
      }

      flushReadyCallbacks(posthog);
    },
  });
}

/**
 * Upgrade PostHog persistence from memory to localStorage after cookie
 * consent is accepted. Call from CookieConsentBanner on "Accept".
 */
export function upgradePostHogPersistence() {
  const posthog = postHogIfLoaded();
  if (!posthog || !isBrowserAnalyticsCaptureAllowed()) {
    // The banner calls this right after initPostHog(), which now returns before
    // the SDK chunk has arrived. Apply the upgrade once the SDK is ready.
    if (mayRetainDeferredWork()) persistenceUpgradeRequested = true;
    return;
  }
  if (!preparePostHogPersistence(import.meta.env.VITE_POSTHOG_KEY)) return;
  posthog.set_config({ persistence: "localStorage" });
}

// ---------------------------------------------------------------------------
// Super properties — components register these from mount effects, which on a
// fresh page load run before the consent-gated SDK is ready.
// ---------------------------------------------------------------------------

/**
 * Register super properties now if the SDK is initialized, otherwise hold
 * them (latest value per key wins) and register them the moment the SDK is
 * ready, before any queued event is flushed.
 */
export function registerPostHogProperties(properties: Record<string, unknown>): void {
  const posthog = postHogIfLoaded();
  if (posthog) {
    posthog.register(properties);
    return;
  }
  if (!mayRetainDeferredWork()) return;
  pendingSuperProperties = { ...(pendingSuperProperties ?? {}), ...properties };
}

// ---------------------------------------------------------------------------
// User identification — call on auth state change for cohort analysis
// ---------------------------------------------------------------------------

/**
 * Hash an email for PostHog cohort grouping without sending PII.
 * Not cryptographic — just for bucketing.
 */
function hashEmail(email: string): string {
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    hash = ((hash << 5) - hash) + email.charCodeAt(i);
    hash |= 0;
  }
  return `user_${Math.abs(hash)}`;
}

function applyIdentity(posthog: PostHog, identity: PreparedIdentity): void {
  // SOC-09: Use centralized attribution for first-touch person properties
  const { first_touch } = getAttribution();
  const initialProps: Record<string, string> = {
    initial_utm_source: first_touch.source,
    initial_utm_medium: first_touch.medium,
    initial_utm_campaign: first_touch.campaign,
  };
  posthog.identify(identity.userId, identity.properties, initialProps);
}

/**
 * Call on sign-in / sign-up to associate events with a user.
 *
 * Auth resolves through async Supabase calls and usually lands before the
 * consent-gated SDK chunk has arrived (and before audience eligibility has
 * settled). Rather than dropping the identity, hold the latest one and apply
 * it as soon as the SDK is ready and capture is allowed.
 */
export function identifyUser(
  userId: string,
  properties?: IdentifyProperties,
) {
  if (!isSafeBrowserTelemetryIdentity(userId)) return;
  const identity: PreparedIdentity = {
    userId,
    properties: {
      created_at: properties?.created_at,
      has_paid: properties?.has_paid,
      // Never send or retain a raw email — hash it at the boundary
      email: properties?.email ? hashEmail(properties.email) : undefined,
    },
  };

  const posthog = postHogIfLoaded();
  if (posthog && isBrowserAnalyticsCaptureAllowed()) {
    pendingIdentity = null;
    applyIdentity(posthog, identity);
    return;
  }
  if (!mayRetainDeferredWork()) return;
  pendingIdentity = identity;
}

/** Call on sign-out to disassociate the user from future events. */
export function resetUser() {
  // A sign-out before the SDK is ready must not identify the user afterwards.
  pendingIdentity = null;
  postHogIfLoaded()?.reset();
}
